
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check, UserMinus, Crown, User, Users, Clock, Ban, AlertTriangle } from 'lucide-react'
import { Body, Modal, Button, ButtonVariant } from '@meiuca/ui-components'
import { Participant, ParticipationStatus, ParticipantRole, Game } from '@/types/game'
import { SegmentedControl } from './SegmentedControl'
import { InviteUserSection } from './InviteUserSection'
import { canApproveParticipants } from '@/utils/gameUtils'

interface ParticipantsListProps {
  participants: Participant[]
  game: Game
  isAdmin: boolean
  confirmedCount: number
  isActionLoading?: string | null
  onManageParticipant?: (participantId: string, action: 'approve' | 'reject' | 'remove') => void
  onInviteUser?: (gameId: string, userId: string) => Promise<void>
  onInviteGuest?: (gameId: string, name: string, phoneNumber?: string) => Promise<void>
}

export function ParticipantsList({ participants, game, isAdmin, confirmedCount, isActionLoading, onManageParticipant, onInviteUser, onInviteGuest }: ParticipantsListProps) {
  const [activeTab, setActiveTab] = useState<'confirmed' | 'invited' | 'canceled'>('confirmed')
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ open: boolean; participant: Participant | null }>({
    open: false,
    participant: null
  })
  
  // Usa helper centralizado para verificar se pode aprovar participantes
  const canApprove = canApproveParticipants(game)
  
  const confirmedParticipants = participants.filter(p => p.status === ParticipationStatus.CONFIRMED)
  const invitedParticipants = participants.filter(p => p.status === ParticipationStatus.INVITED)
  const canceledParticipants = participants.filter(p => p.status === ParticipationStatus.CANCELED)

  const tabs = [
    { 
      id: 'confirmed' as const, 
      label: 'Confirmados', 
      count: confirmedCount, 
      icon: Users,
      color: 'text-status-success-text bg-status-success-bg border-status-success-border',
      participants: confirmedParticipants
    },
    { 
      id: 'invited' as const, 
      label: 'Aguardando', 
      count: invitedParticipants.length, 
      icon: Clock,
      color: 'text-status-warning-text bg-status-warning-bg border-status-warning-border',
      participants: invitedParticipants
    },
    { 
      id: 'canceled' as const, 
      label: 'Cancelaram', 
      count: canceledParticipants.length, 
      icon: Ban,
      color: 'text-text-secondary bg-background-secondary border-gray-200',
      participants: canceledParticipants
    }
  ]

  const activeTabData = tabs.find(tab => tab.id === activeTab)
  const currentParticipants = activeTabData?.participants || []

  const handleDeleteClick = (participant: Participant) => {
    setDeleteConfirmModal({ open: true, participant })
  }

  const handleDeleteConfirm = () => {
    if (deleteConfirmModal.participant && onManageParticipant) {
      onManageParticipant(deleteConfirmModal.participant.id, 'remove')
    }
    setDeleteConfirmModal({ open: false, participant: null })
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ open: false, participant: null })
  }

  const ParticipantCard = ({ participant }: { participant: Participant }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-background-white border border-border-primary rounded-lg hover:shadow-sm transition-all space-y-3 sm:space-y-0"
    >
      <div className="flex items-center space-x-3 min-w-0 flex-1">
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 bg-brand-secondary rounded-full flex items-center justify-center">
            {participant.avatar ? (
              <img 
                src={participant.avatar} 
                alt={participant.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <User className="w-5 h-5 text-white" />
            )}
          </div>
          {participant.role === ParticipantRole.ADMIN && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
              <Crown className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
            <Body className="font-medium text-text-primary truncate">{participant.name}</Body>
            {participant.nickname && (
              <Body className="text-sm text-text-secondary truncate">@{participant.nickname}</Body>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {participant.role === ParticipantRole.OWNER && (
              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center space-x-1 whitespace-nowrap">
                <Crown className="w-3 h-3" />
                <span>Organizador</span>
              </span>
            )}
            {participant.role === ParticipantRole.ADMIN && (
              <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full whitespace-nowrap">Admin</span>
            )}
          </div>
        </div>
      </div>

      {isAdmin && onManageParticipant && participant.role !== ParticipantRole.ADMIN && participant.role !== ParticipantRole.OWNER && (
        <div className="flex items-center justify-end sm:justify-center space-x-1 sm:space-x-2 flex-shrink-0 w-full sm:w-auto">
          {/* Botão Confirmar: disponível para INVITED e CANCELED */}
          {(participant.status === ParticipationStatus.INVITED || participant.status === ParticipationStatus.CANCELED) && (
            <Button
              variant={ButtonVariant.POSITIVE}
              size="sm"
              onClick={() => onManageParticipant(participant.id, 'approve')}
              loading={isActionLoading === `manage-${participant.id}-approve`}
              title={canApprove ? "Confirmar participação" : "Jogo lotado"}
              className="flex-1 sm:flex-none"
              disabled={!canApprove}
            >
              <Check className="w-4 h-4" />
              <span className="sm:hidden ml-1">{canApprove ? "Confirmar" : "Lotado"}</span>
            </Button>
          )}
          
          {/* Botão Cancelar: disponível apenas para CONFIRMED */}
          {participant.status === ParticipationStatus.CONFIRMED && (
            <Button
              variant={ButtonVariant.WARNING}
              buttonStyle="filled"
              size="sm"
              onClick={() => onManageParticipant(participant.id, 'reject')}
              loading={isActionLoading === `manage-${participant.id}-reject`}
              title="Cancelar participação"
              className="flex-1 sm:flex-none"
            >
              <Ban className="w-4 h-4" />
              <span className="sm:hidden ml-1">Cancelar</span>
            </Button>
          )}
          
          {/* Botão Deletar: disponível para todos os status */}
          <Button
            variant={ButtonVariant.NEGATIVE}
            size="sm"
            onClick={() => handleDeleteClick(participant)}
            loading={isActionLoading === `manage-${participant.id}-remove`}
            title="Deletar participante definitivamente"
            className="flex-1 sm:flex-none"
          >
            <UserMinus className="w-4 h-4" />
            <span className="sm:hidden ml-1">Deletar</span>
          </Button>
        </div>
      )}
    </motion.div>
  )

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-background-white rounded-xl shadow-sm p-6 border border-border-primary"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">Participantes</h3>
        <div className="text-sm text-text-secondary">
          {participants.length} {participants.length === 1 ? 'pessoa' : 'pessoas'}
        </div>
      </div>

      {/* Invite User Section */}
      <InviteUserSection 
        game={game}
        isAdmin={isAdmin} 
        onInviteUser={onInviteUser}
        onInviteGuest={onInviteGuest}
        isActionLoading={isActionLoading}
      />
      
      {/* Tabs */}
      <SegmentedControl
        options={tabs.map(tab => ({
          id: tab.id,
          label: tab.label,
          icon: tab.icon,
          count: tab.count,
          color: tab.color
        }))}
        activeOption={activeTab}
        onChange={(tabId) => setActiveTab(tabId as 'confirmed' | 'invited' | 'canceled')}
        className="mb-6"
      />

      {/* Content */}
      <div className="space-y-3">
        {currentParticipants.length === 0 ? (
          <div className="text-center py-12">
            {activeTabData && (
              <div className="w-16 h-16 bg-background-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <activeTabData.icon className="w-8 h-8 text-text-secondary" />
              </div>
            )}
            <h4 className="text-lg font-medium text-text-primary mb-2">
              Nenhum participante {activeTabData?.label.toLowerCase()}
            </h4>
            <Body className="text-text-secondary">
              {activeTab === 'confirmed' && 'Ainda não há participantes confirmados'}
              {activeTab === 'invited' && 'Não há convites pendentes'}
              {activeTab === 'canceled' && 'Nenhum participante cancelou'}
            </Body>
          </div>
        ) : (
          currentParticipants.map((participant, index) => (
            <motion.div
              key={participant.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <ParticipantCard participant={participant} />
            </motion.div>
          ))
        )}
      </div>

      {participants.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-background-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-text-secondary" />
          </div>
          <h4 className="text-lg font-medium text-text-primary mb-2">Nenhum participante</h4>
          <Body className="text-text-secondary">Este jogo ainda não tem participantes</Body>
        </div>
      )}

      {/* Modal de Confirmação de Deleção */}
      <Modal
        open={deleteConfirmModal.open}
        onClose={handleDeleteCancel}
        title="Deletar Participante"
        headerIcon={AlertTriangle}
        size="sm"
        className="text-center"
        appearance={{
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          overlayColor: 'rgba(0, 0, 0, 0.6)'
        }}
      >
        <div className="space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserMinus className="w-8 h-8 text-red-600" />
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Tem certeza?
            </h3>
            <Body className="text-gray-600 mb-4">
              Você está prestes a deletar definitivamente o participante{' '}
              <strong className="text-gray-900">
                {deleteConfirmModal.participant?.name}
              </strong>
              {' '}do jogo.
            </Body>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <Body className="text-red-700 text-sm">
                ⚠️ Esta ação não pode ser desfeita e o participante será removido permanentemente.
              </Body>
            </div>
          </div>

          <div className="flex space-x-3 justify-center">
            <Button
              variant={ButtonVariant.PRIMARY}
              buttonStyle="clear"
              onClick={handleDeleteCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant={ButtonVariant.NEGATIVE}
              buttonStyle="filled"
              onClick={handleDeleteConfirm}
              className="flex-1"
            >
              Deletar
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  )
}